<template>
    <card-tab-panel
        without-padding
        name="appointment"
        v-bind="$attrs"
        with-variable-height
    >
        <flex-container
            align-items="center"
            :padding="Size.md"
        >
            <input-group v-model="responsibleFilterToggleValue">
                <template #input>
                    <responsible-toggle-input
                        label="afspraken"
                        :count-active="myAppointmentCount"
                        :count-inactive="myTeamAppointmentCount"
                    />
                </template>
            </input-group>
        </flex-container>

        <appointment-list
            :responsible-filter="responsibleFilter.value"
            :show-all="showAll"
            :appointments="props.appointmentCollection?.data.value ?? []"
        >
            <template #empty-message>
                <text-decorator :size="FontSize.sm">
                    <template v-if="lead">
                        Er zijn geen afspraken gevonden voor deze lead in "{{ responsibleFilter.label }}".
                    </template>
                    <template v-else>
                        Er zijn geen afspraken gevonden in "{{ responsibleFilter.label }}".
                    </template>
                </text-decorator>
            </template>
            <template #footer-center>
                <button-text
                    v-if="props.lead"
                    :disabled="!props.editable"
                    @click="router.push(createRouteLocation({
                        name: 'ActionCreate',
                        params: {
                            leadId: props.lead.id.value,
                        },
                    }, 'lead'))"
                >
                    <svg-icon :icon="IconAdd" />
                    <span>Afspraak toevoegen</span>
                </button-text>
                <contact-create-action-collapse v-else>
                    <span>Afspraak toevoegen</span>
                </contact-create-action-collapse>
            </template>
            <template #footer-right="{ bindings }">
                <list-footer-action-show-more
                    v-if="bindings.count > 4"
                    @click="showAll = !showAll"
                >
                    <template v-if="showAll">
                        Bekijk minder
                    </template>
                    <template v-else>
                        Bekijk alle {{ bindings.count }}
                    </template>
                </list-footer-action-show-more>
            </template>
        </appointment-list>
    </card-tab-panel>
</template>

<script setup lang="ts">
    import {
        ButtonText,
        CardTabPanel,
        FlexContainer,
        FontSize,
        InputGroup,
        ListFooterActionShowMore,
        Size,
        SvgIcon,
    } from '@keyvue/one-ui';

    import AppointmentList from '@components/List/AppointmentList.vue';
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import type { Appointment, Lead } from '@keyvue/one-api-client';
    import { DashboardView } from '@keyvue/one-api-client-legacy';
    import { ref, computed } from 'vue';
    import useClient, { type ReactiveCollection } from '@composables/useClient';
    import { useRouter } from 'vue-router';
    import useParentRoute from '@composables/useParentRoute';
    import ContactCreateActionCollapse from '@components/Collapses/ContactCreateActionCollapse.vue';
    import ResponsibleToggleInput from '@components/Inputs/ResponsibleToggleInput.vue';

    export interface Props {
        editable?: boolean
        lead?: Lead
        appointmentCollection: ReactiveCollection<Appointment>;
    }

    const props = defineProps<Props>();
    const { currentUser, currentLegacyUser } = useClient();
    const router = useRouter();
    const createRouteLocation = useParentRoute();

    const showAll = ref(false);
    const responsibleFilterDefault: { value: 'user' | 'team', label: string } = currentLegacyUser.value?.defaultDashboardView === DashboardView.BRANCHES
        ? { value: 'team', label: 'Mijn team afspraken' }
        : { value: 'user', label: 'Mijn afspraken' };
    const responsibleFilter = ref<{ value: 'user' | 'team', label: string }>(responsibleFilterDefault);

    const responsibleFilterToggleValue = computed({
        get() {
            return responsibleFilter.value.value === 'user';
        },
        set(checked) {
            responsibleFilter.value = checked
                ? { value: 'user', label: 'Mijn afspraken' }
                : { value: 'team', label: 'Mijn team afspraken' };
        },
    });

    // Responsible filters
    const myAppointmentsFilter = (appointment: Appointment) => appointment.assignedUsers.map((user) => user.id.toString()).includes(currentUser.value?.id.toString() ?? '');
    const myTeamAppointmentsFilter = (appointment: Appointment) => !appointment.assignedUsers.map((user) => user.id.toString()).includes(currentUser.value?.id.toString() ?? '');

    // Counts based on current responsible filter
    const myAppointmentCount = computed(() => props.appointmentCollection.data.value.filter(myAppointmentsFilter).length);
    const myTeamAppointmentCount = computed(() => props.appointmentCollection.data.value.filter(myTeamAppointmentsFilter).length);
</script>
